/**
 * What this tenant is entitled to, as decided by Olive staff in the admin UI.
 *
 * Separate from `tenantConfigurationSlice` on purpose. That one is fetched **before login** from an
 * unauthenticated endpoint so the login redirect can read it, and it is sourced from the tenant's
 * own `configurations` table. These flags live on the platform `tenants` row, which that endpoint
 * cannot see, and entitlement is not something an anonymous caller should be able to read.
 *
 * Fails open to "nothing enabled": a backend without the endpoint, or a request that fails, simply
 * hides the gated features rather than blocking the app. Nothing here gates the login path.
 */

import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";
import { STATUS } from "@/constants";
import { apiFetch } from "@/shared/api/apiFetch";

/** Payload from `GET /v1/tenants/features`. */
export type TenantFeatures = {
  nugget_schedule_enabled: boolean;
  scheduled_jobs_enabled: boolean;
  enabled_job_types: string[];
  sso_role_assignment_rules_configured: boolean;
};

export type TenantFeaturesState = {
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  features: TenantFeatures | null;
};

export const tenantFeaturesInitialState: TenantFeaturesState = {
  status: STATUS.IDLE,
  error: null,
  features: null,
};

function isTenantFeatures(value: unknown): value is TenantFeatures {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    // Absent is accepted, not rejected: a backend that has not shipped `nugget_schedule_enabled`
    // yet omits the field, and failing the guard here would reject the whole payload — taking the
    // Jobs tab down with it. `normalizeTenantFeatures` fills the gap.
    (row.nugget_schedule_enabled === undefined ||
      typeof row.nugget_schedule_enabled === "boolean") &&
    typeof row.scheduled_jobs_enabled === "boolean" &&
    Array.isArray(row.enabled_job_types) &&
    row.enabled_job_types.every((jobType) => typeof jobType === "string") &&
    typeof row.sso_role_assignment_rules_configured === "boolean"
  );
}

/** Fills fields the guard tolerates as absent, so stored state always matches the type. */
function normalizeTenantFeatures(features: TenantFeatures): TenantFeatures {
  return {
    ...features,
    nugget_schedule_enabled: features.nugget_schedule_enabled === true,
  };
}

export const fetchTenantFeatures = createAsyncThunk<
  TenantFeatures,
  void,
  { rejectValue: string }
>("tenantFeatures/fetch", async (_arg, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/tenants/features", { method: "GET" });
    if (!res.ok) {
      return rejectWithValue(`Request failed (${res.status})`);
    }
    const data: unknown = await res.json();
    if (!isTenantFeatures(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return normalizeTenantFeatures(data);
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : "Request failed");
  }
});

const tenantFeaturesSlice = createSlice({
  name: "tenantFeatures",
  initialState: tenantFeaturesInitialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchTenantFeatures.pending, (state) => {
        state.status = STATUS.LOADING;
        state.error = null;
      })
      .addCase(fetchTenantFeatures.fulfilled, (state, action) => {
        state.status = STATUS.SUCCEEDED;
        state.features = action.payload;
        state.error = null;
      })
      .addCase(fetchTenantFeatures.rejected, (state, action) => {
        // Left null rather than defaulted: the selectors already read an absent payload as
        // "nothing enabled", so one place decides what a failed fetch means.
        state.status = STATUS.FAILED;
        state.features = null;
        state.error = action.payload ?? null;
      });
  },
});

export const tenantFeaturesReducer = tenantFeaturesSlice.reducer;

const selectTenantFeatures = (state: { tenantFeatures: TenantFeaturesState }) =>
  state.tenantFeatures.features;

/**
 * Scheduled-job types this tenant may use, or empty while unresolved.
 *
 * Empty until the fetch settles, which makes gating fail-closed the same way `usePermissions` does:
 * a gated tab appears once entitlement is known rather than flashing and disappearing.
 */
export const selectEnabledJobTypes = createSelector(
  [selectTenantFeatures],
  (features): string[] => features?.enabled_job_types ?? [],
);

/** Whether any job type is enabled — what decides if the Jobs tab exists at all. */
export const selectHasAnyJobTypeEnabled = createSelector(
  [selectEnabledJobTypes],
  (enabledJobTypes) => enabledJobTypes.length > 0,
);

/**
 * Whether Olive staff has switched nugget schedules on — what decides if the Nugget Configs tab
 * exists at all, and whether the nugget-config write endpoints will answer with anything but 403.
 *
 * `=== true` rather than a truthiness check gives the same fail-closed-until-settled behaviour as
 * the job-type selector above: a failed fetch leaves `features` null and reads as "not enabled".
 */
export const selectNuggetScheduleEnabled = createSelector(
  [selectTenantFeatures],
  (features) => features?.nugget_schedule_enabled === true,
);

/** Whether the tenant has authored at least one SSO role-assignment rule. */
export const selectSsoRoleAssignmentRulesConfigured = createSelector(
  [selectTenantFeatures],
  (features) => features?.sso_role_assignment_rules_configured === true,
);
